import { AlertTriangle, CalendarClock, ClipboardEdit, Paperclip, Target } from 'lucide-react';
import { Link } from 'react-router';
import type { BlocPlanTableau, ElementCourtPlan } from '@/api/types';
import { Kpi, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn, dateFr, montant, nombre, pct } from '@/lib/utils';
import { Avancement } from '@/pages/plan/PlanListe';

const TITRE: Record<BlocPlanTableau['profil'], string> = {
  DIRECTION: 'Vision du plan opérationnel',
  DGA: 'Plan opérationnel des entités supervisées',
  DF: 'Plan opérationnel · lecture croisée',
  CG: 'Qualité du suivi du plan opérationnel',
  DIR: 'Plan opérationnel de la direction',
  CM: 'Sous-activités du Mémorial',
  RESPONSABLE: 'Mes sous-activités',
};

function ListeCourte({ titre, elements, lien, vide, icone: Icone, accent }: { titre: string; elements: ElementCourtPlan[]; lien: string; vide: string; icone: typeof ClipboardEdit; accent?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className={cn('flex items-center gap-1.5 text-sm font-medium', accent)}>
          <Icone className="size-4" /> {titre}
          <Badge variant={elements.length ? 'secondary' : 'outline'}>{elements.length}</Badge>
        </div>
        <Link to={lien} className="text-xs text-primary hover:underline">
          Voir la liste
        </Link>
      </div>
      {!elements.length ? (
        <p className="text-xs text-muted-foreground">{vide}</p>
      ) : (
        <ul className="space-y-0.5 text-xs">
          {elements.slice(0, 5).map((e) => (
            <li key={e.ligneId} className="flex items-center gap-2">
              <Link to={`/plan/${e.ligneId}`} className="font-medium hover:underline">
                {e.code}
              </Link>
              <span className="truncate text-muted-foreground">{e.libelle}</span>
              {e.echeance && <span className="ml-auto whitespace-nowrap text-amber-700">échéance {dateFr(e.echeance)}</span>}
              {e.jours !== undefined && !e.echeance && <span className="ml-auto whitespace-nowrap text-red-700">{e.jours} j</span>}
            </li>
          ))}
          {elements.length > 5 && <li className="text-muted-foreground">… et {elements.length - 5} autre(s).</li>}
        </ul>
      )}
    </div>
  );
}

/** Bloc « Plan opérationnel » du tableau de bord, composé selon le profil (spécifications « Intégration du PTBA », § 6.6). */
export function BlocPlan({ bloc }: { bloc: BlocPlanTableau }) {
  const s = bloc.synthese;
  const base = `/plan?exercice=${bloc.exercice}`;
  const lienEntite = (code: string, suffixe = '') => `${base}&structure=${code}${suffixe}`;
  return (
    <Card data-testid="bloc-plan">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <Target className="size-5" /> {TITRE[bloc.profil]} {bloc.exercice}
          {s.retards > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="size-3" /> {s.retards} en retard
            </Badge>
          )}
          {s.enAttente > 0 && <Badge variant="secondary">{s.enAttente} à valider</Badge>}
        </CardTitle>
        <CardDescription>
          {bloc.perimetre} · avancement physique mesuré par indicateurs et jalons validés ; seules les mises à jour validées comptent ({s.enAttente} en attente).{' '}
          <Link to={base} className="text-primary hover:underline">
            Ouvrir le plan opérationnel
          </Link>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {bloc.profil !== 'RESPONSABLE' && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Kpi label="Avancement" valeur={pct(s.avancement, 0)} detail={`couverture ${pct(s.couverture, 0)} (${s.renseignees}/${s.total})`} />
            <Kpi label="En cours" valeur={s.parStatut.EN_COURS} detail={`${s.parStatut.TERMINEE} terminée(s) · ${s.parStatut.PREVUE + s.parStatut.NON_DEMARREE} non démarrée(s)`} />
            <Kpi label="En retard" valeur={s.retards} accent={s.retards ? 'alerte' : undefined} />
            <Kpi label="À mettre à jour" valeur={s.aMettreAJour} accent={s.aMettreAJour ? 'alerte' : undefined} />
            <Kpi label="À valider" valeur={s.enAttente} />
            <Kpi label="Exécution financière" valeur={pct(s.tauxExecution, 0)} detail={`${montant(s.paye)} payés · ${s.aExaminer} écart(s) à examiner`} />
          </div>
        )}

        {(bloc.profil === 'DIRECTION' || bloc.profil === 'DGA' || bloc.profil === 'DF' || bloc.profil === 'CG' || bloc.profil === 'CM') && bloc.entites.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Entité</TableHead>
                <TableHead>Avancement physique</TableHead>
                <TableHead className="text-right">Exécution financière</TableHead>
                <TableHead className="text-right">Écart (pts)</TableHead>
                <TableHead className="text-right">Retards</TableHead>
                <TableHead className="text-right">À valider</TableHead>
                <TableHead className="text-right">À mettre à jour</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bloc.entites.map((e) => (
                <TableRow key={e.structureCode}>
                  <TableCell>
                    <Link to={lienEntite(e.structureCode)} className="font-medium hover:underline">
                      {e.structureCode}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {e.structureLibelle} · {e.renseignees}/{e.total} renseignée(s)
                    </div>
                  </TableCell>
                  <TableCell>
                    <Avancement valeur={e.avancement} petite />
                  </TableCell>
                  <TableCell className="num text-right">{pct(e.tauxExecution, 0)}</TableCell>
                  <TableCell className={cn('num text-right', e.aExaminer > 0 && 'font-medium text-amber-700')} title={e.aExaminer ? `${e.aExaminer} écart(s) à examiner` : undefined}>
                    {e.ecart === null ? '—' : `${e.ecart > 0 ? '+' : ''}${nombre(e.ecart)}`}
                  </TableCell>
                  <TableCell className={cn('num text-right', e.retards > 0 && 'font-medium text-red-700')}>
                    <Link to={lienEntite(e.structureCode, '&retard=1')}>{e.retards}</Link>
                  </TableCell>
                  <TableCell className="num text-right">
                    <Link to={lienEntite(e.structureCode, '&enAttente=1')}>{e.enAttente}</Link>
                  </TableCell>
                  <TableCell className="num text-right">
                    <Link to={lienEntite(e.structureCode, '&aMettreAJour=1')}>{e.aMettreAJour}</Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {bloc.profil === 'DIR' && (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <ListeCourte titre="À valider" elements={bloc.miennes.enAttente} lien={`${base}&enAttente=1`} vide="Aucune mise à jour soumise." icone={ClipboardEdit} />
              <ListeCourte titre="En retard" elements={bloc.miennes.retards} lien={`${base}&retard=1`} vide="Aucune sous-activité en retard." icone={AlertTriangle} accent="text-red-700" />
              <ListeCourte titre="Non renseignées ou à paramétrer" elements={[]} lien={`${base}&aParametrer=1`} vide={`${bloc.qualite.nonRenseignees} sous-activité(s) sans mise à jour validée, ${bloc.qualite.sansMethode} sans suivi paramétré.`} icone={Target} />
            </div>
            {bloc.activites.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Activité</TableHead>
                    <TableHead>Avancement</TableHead>
                    <TableHead className="text-right">Sous-activités</TableHead>
                    <TableHead className="text-right">Retards</TableHead>
                    <TableHead className="text-right">À valider</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bloc.activites.map((a) => (
                    <TableRow key={a.activiteCode ?? 'nc'}>
                      <TableCell>
                        <Link to={`${base}${a.activiteCode ? `&activite=${a.activiteCode}` : ''}`} className="font-medium hover:underline">
                          {a.activiteLibelle ?? 'Non classée'}
                        </Link>
                        {a.activiteCode && <span className="ml-2 font-mono text-xs text-muted-foreground">{a.activiteCode}</span>}
                      </TableCell>
                      <TableCell>
                        <Avancement valeur={a.avancement} petite />
                      </TableCell>
                      <TableCell className="num text-right">
                        {a.renseignees}/{a.total}
                      </TableCell>
                      <TableCell className={cn('num text-right', a.retards > 0 && 'text-red-700')}>{a.retards}</TableCell>
                      <TableCell className="num text-right">{a.enAttente}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}

        {bloc.profil === 'RESPONSABLE' && (
          <div className="grid gap-3 md:grid-cols-2">
            <ListeCourte titre="Mises à jour à faire" elements={bloc.miennes.aMettreAJour} lien={`${base}&responsable=moi&aMettreAJour=1`} vide="Aucune mise à jour attendue." icone={CalendarClock} accent="text-amber-700" />
            <ListeCourte titre="Compléments demandés" elements={bloc.miennes.complements} lien={`${base}&responsable=moi`} vide="Aucun complément demandé." icone={ClipboardEdit} accent="text-red-700" />
            <ListeCourte titre="En retard" elements={bloc.miennes.retards} lien={`${base}&responsable=moi&retard=1`} vide="Aucune sous-activité en retard." icone={AlertTriangle} accent="text-red-700" />
            <ListeCourte titre="Justificatifs attendus" elements={bloc.miennes.justificatifsAttendus} lien={`${base}&responsable=moi`} vide="Chaque mise à jour déclarée a au moins une pièce." icone={Paperclip} />
          </div>
        )}

        {(bloc.profil === 'DIRECTION' || bloc.profil === 'DGA') && (
          <div className="rounded-lg border p-3">
            <div className="mb-1 text-sm font-medium">Difficultés signalées (mises à jour validées des deux dernières périodes)</div>
            {!bloc.difficultes.length ? (
              <p className="text-xs text-muted-foreground">Aucune difficulté signalée.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {bloc.difficultes.map((d, k) => (
                  <li key={`${d.ligneId}-${k}`}>
                    <Link to={`/plan/${d.ligneId}`} className="font-medium hover:underline">
                      {d.structureCode} · {d.code}
                    </Link>{' '}
                    <span className="text-muted-foreground">({d.periode})</span> : {d.texte}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {bloc.profil === 'DF' && (
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border p-3">
              <div className="mb-1 text-sm font-medium">Besoins de financement à venir</div>
              <p className="mb-2 text-xs text-muted-foreground">Plan mensuel des sous-activités en cours ou non démarrées (1/12 du budget sans plan saisi).</p>
              {!bloc.besoins.length ? (
                <Vide message="Aucun mois restant sur l’exercice." />
              ) : (
                <ul className="space-y-0.5 text-sm">
                  {bloc.besoins.map((b) => (
                    <li key={b.mois} className="flex justify-between">
                      <span className="capitalize">{b.libelle}</span>
                      <span className="num font-medium">{montant(b.montant)}</span>
                    </li>
                  ))}
                  <li className="text-xs text-muted-foreground">{bloc.besoins[0]!.sousActivites} sous-activité(s) concernée(s)</li>
                </ul>
              )}
            </div>
            <ListeCourte titre="Écarts physique / financier à examiner" elements={[]} lien={`${base}`} vide={`${s.aExaminer} sous-activité(s) avec plus de 30 points d’écart (hors avances et paiements à la livraison).`} icone={AlertTriangle} accent={s.aExaminer ? 'text-amber-700' : undefined} />
          </div>
        )}

        {bloc.profil === 'CG' && (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Couverture du suivi" valeur={pct(bloc.qualite.couverture, 0)} detail={`${bloc.qualite.nonRenseignees} non renseignée(s)`} />
            <Kpi label="Fraîcheur moyenne" valeur={bloc.qualite.fraicheurMoyenneJours === null ? '—' : `${bloc.qualite.fraicheurMoyenneJours} j`} detail="depuis la dernière validation" />
            <Kpi label="Sans méthode ou cible" valeur={bloc.qualite.sansMethode} accent={bloc.qualite.sansMethode ? 'alerte' : undefined} detail="suivi à paramétrer" />
            <Kpi label="Sans responsable" valeur={bloc.qualite.sansResponsable} />
            <Kpi label="Écarts à examiner" valeur={bloc.qualite.ecarts} accent={bloc.qualite.ecarts ? 'alerte' : undefined} detail="physique / financier" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
